import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { client } from "@/utils/orpc";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@super-resume-new/ui/components/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@super-resume-new/ui/components/table";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/admin/reports")({
  head: () =>
    seo({
      title: "Flagged Reports | JobCues Admin",
      description: "Review user-submitted reports for broken, expired, or misleading job listings.",
      path: "/admin/reports",
      noindex: true,
    }),
  component: AdminReportsPage,
});

function AdminReportsPage() {
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const {
    data: reportsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["admin", "reports", page],
    queryFn: () => client.admin.listReports({ page, limit: pageSize }),
  });

  const resolveMutation = useMutation({
    mutationFn: async ({
      reportId,
      action,
    }: {
      reportId: string;
      action: "dismiss" | "expire_job" | "delete_job";
    }) => {
      return client.admin.resolveReport({ reportId, action });
    },
    onSuccess: () => {
      toast.success("Report handled");
      refetch();
    },
    onError: (err: any) => toast.error(err.message || "Failed to handle report"),
  });

  const total = reportsData?.total ?? 0;
  const totalPages = reportsData?.totalPages ?? 1;

  return (
    <div className="space-y-4">
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-base font-semibold">
            User Flagged Job Reports {total > 0 && `(${total})`}
          </CardTitle>
          <CardDescription className="text-xs">
            Review listings reported by candidates for expired positions, broken links, or
            misleading details.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reported Job</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Reported By</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                    <Loader2 className="mx-auto size-6 animate-spin text-brand mb-2" />
                    Loading reports…
                  </TableCell>
                </TableRow>
              ) : reportsData?.reports?.length ? (
                reportsData.reports.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-semibold text-foreground">
                          {r.jobTitle || "Deleted Job"}
                        </span>
                        <span className="text-xs text-muted-foreground">{r.jobCompany}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="bg-amber-500/10 text-amber-500 border-amber-500/30 uppercase text-[11px]"
                      >
                        {r.reason}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-xs truncate">
                      {r.notes || "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.userEmail || "Anonymous visitor"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            resolveMutation.mutate({ reportId: r.id, action: "expire_job" })
                          }
                          className="h-8 text-xs font-semibold text-amber-500"
                        >
                          Expire Job
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            resolveMutation.mutate({ reportId: r.id, action: "dismiss" })
                          }
                          className="h-8 text-xs"
                        >
                          Dismiss
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-muted-foreground text-sm">
                    No active job reports. Everything looks healthy!
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="gap-1"
          >
            <ChevronLeft className="size-3.5" /> Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="gap-1"
          >
            Next <ChevronRight className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
